import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Divider,
  Avatar,
  FormControlLabel,
  Switch,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PrintIcon from '@mui/icons-material/Print';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import ManagerApi from '../../api/ManagerApi';
import { CreateCourtDto } from '../../interfaces/CourtInterfaces';
import './ManagerCreateCourtView.scss';

interface FormData {
  Court_Name: string;
  Court_Email: string;
  Court_Address: string;
  Court_Phone: string;
  Court_Fax: string;
  Court_Active: boolean;
}

interface FormErrors {
  Court_Name?: string;
  Court_Email?: string;
  Court_Address?: string;
  Court_Phone?: string;
  Court_Fax?: string;
}

const initialFormData: FormData = {
  Court_Name: '',
  Court_Email: '',
  Court_Address: '',
  Court_Phone: '',
  Court_Fax: '',
  Court_Active: true
};

const ManagerCreateCourtView: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate Court Name
    if (!formData.Court_Name.trim()) {
      newErrors.Court_Name = 'Court name is required';
    } else if (formData.Court_Name.trim().length < 1 || formData.Court_Name.trim().length > 100) {
      newErrors.Court_Name = 'Court name must be between 1 and 100 characters';
    }

    // Validate Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.Court_Email.trim()) {
      newErrors.Court_Email = 'Court email is required';
    } else if (!emailRegex.test(formData.Court_Email.trim())) {
      newErrors.Court_Email = 'Please enter a valid email address';
    }

    // Validate Address
    if (!formData.Court_Address.trim()) {
      newErrors.Court_Address = 'Court address is required';
    } else if (formData.Court_Address.trim().length < 1 || formData.Court_Address.trim().length > 255) {
      newErrors.Court_Address = 'Court address must be between 1 and 255 characters';
    }

    // Validate Phone (optional)
    if (formData.Court_Phone.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.Court_Phone.trim().replace(/[\s\-\(\)\.]/g, ''))) {
        newErrors.Court_Phone = 'Please enter a valid phone number';
      }
    }

    // Validate Fax (optional)
    if (formData.Court_Fax.trim()) {
      const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      if (!phoneRegex.test(formData.Court_Fax.trim().replace(/[\s\-\(\)\.]/g, ''))) {
        newErrors.Court_Fax = 'Please enter a valid fax number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'Court_Active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSubmitError(null);
    setSuccessMessage(null);

    try {
      const courtData: CreateCourtDto = {
        Court_Name: formData.Court_Name.trim(),
        Court_Email: formData.Court_Email.trim(),
        Court_Address: formData.Court_Address.trim(),
        Court_Phone: formData.Court_Phone.trim() || undefined,
        Court_Fax: formData.Court_Fax.trim() || undefined,
        Court_Active: formData.Court_Active
      };

      const createdCourt = await ManagerApi.createCourt(courtData);
      setSuccessMessage(`Court "${createdCourt.Court_Name}" created successfully!`);
      
      // Reset form
      setFormData(initialFormData);
      setErrors({});

      // Navigate to the created court after a short delay
      setTimeout(() => {
        navigate(`/court?Court_guid=${createdCourt.Court_guid}`);
      }, 2000);

    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create court');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/courts');
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
    setSubmitError(null);
    setSuccessMessage(null);
  };

  return (
    <div className="manager-create-court-view">
      
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back to Courts
          </Button>
          
          <Box flexGrow={1}>
            <Typography variant="h4" component="h1" gutterBottom>
              Create New Court
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a new court to the system with all necessary information.
            </Typography>
          </Box>
        </Box>

        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMessage}
          </Alert>
        )}

        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <AccountBalanceIcon />
              </Avatar>
              <Typography variant="h6" component="h2">
                Court Information
              </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Court Name *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder="Enter court name (e.g., Municipal Court of Austin)"
                  value={formData.Court_Name}
                  onChange={handleInputChange('Court_Name')}
                  error={!!errors.Court_Name}
                  helperText={errors.Court_Name || 'Maximum 100 characters'}
                  disabled={loading}
                  inputProps={{ maxLength: 100 }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Court Email *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="Enter court email address"
                  value={formData.Court_Email}
                  onChange={handleInputChange('Court_Email')}
                  error={!!errors.Court_Email}
                  helperText={errors.Court_Email || 'This email will be used for court communications'}
                  disabled={loading}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Court Phone
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="tel"
                  placeholder="Enter court phone number (optional)"
                  value={formData.Court_Phone}
                  onChange={handleInputChange('Court_Phone')}
                  error={!!errors.Court_Phone}
                  helperText={errors.Court_Phone || 'Optional - Phone number for court contact'}
                  disabled={loading}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <PrintIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Court Fax
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  type="tel"
                  placeholder="Enter court fax number (optional)"
                  value={formData.Court_Fax}
                  onChange={handleInputChange('Court_Fax')}
                  error={!!errors.Court_Fax}
                  helperText={errors.Court_Fax || 'Optional - Used on LOR PDF Via Fax line when set'}
                  disabled={loading}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Court Address *
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Enter full court address"
                  value={formData.Court_Address}
                  onChange={handleInputChange('Court_Address')}
                  error={!!errors.Court_Address}
                  helperText={errors.Court_Address || 'Maximum 255 characters'}
                  disabled={loading}
                  inputProps={{ maxLength: 255 }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.Court_Active}
                      onChange={handleInputChange('Court_Active')}
                      disabled={loading}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        Court Status: {formData.Court_Active ? 'Active' : 'Inactive'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Active courts can receive and process tickets
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                  size="large"
                >
                  Reset Form
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={loading}
                  size="large"
                >
                  {loading ? 'Creating Court...' : 'Create Court'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default ManagerCreateCourtView;
